import { useMemo, useState, type ReactNode } from 'react';
import { CheckCircleOutline, ContentCopy, ErrorOutline, Terminal } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ButtonTabs, { type ButtonTab } from '../ButtonTabs';
import {
  API_REFERENCE_CODE_LANGUAGE_LABELS,
  API_REFERENCE_CODE_LANGUAGE_ORDER,
  API_REFERENCE_EXAMPLES,
  type ApiReferenceCodeLanguage,
  type ApiReferenceExampleId,
  type ApiReferenceExampleResponse,
} from '../../help/apiReferenceExamples';

interface ApiCodeExamplesProps {
  exampleId: ApiReferenceExampleId;
}

interface ApiResponseExamplesProps {
  exampleId: ApiReferenceExampleId;
}

interface HelpCodeBlockProps {
  code: string;
}

/**
 * Render a canonical monospace code surface for API snippets and responses.
 */
function HelpCodeBlock({ code }: HelpCodeBlockProps) {
  return (
    <Box
      component="pre"
      data-help-code-block="true"
      sx={{
        m: 0,
        px: 2,
        py: 2.5,
        overflowX: 'auto',
        borderTop: '1px solid',
        borderColor: 'divider',
        fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, "Liberation Mono", monospace',
        fontSize: 13,
        lineHeight: 1.7,
        bgcolor: 'rgba(11, 16, 24, 0.96)',
        color: 'grey.100',
      }}
    >
      <Box component="code">{code}</Box>
    </Box>
  );
}

/**
 * Shared snippet shell that keeps the help API docs consistent across request
 * and response examples.
 */
function ApiReferenceCard({
  title,
  currentSnippet,
  copyLabel,
  copySuccessLabel,
  copyErrorLabel,
  children,
}: {
  title: string;
  currentSnippet: string;
  copyLabel: string;
  copySuccessLabel: string;
  copyErrorLabel: string;
  children: ReactNode;
}) {
  const [copyFeedback, setCopyFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const handleCopy = async () => {
    if (!currentSnippet) {
      return;
    }
    try {
      await navigator.clipboard.writeText(currentSnippet);
      setCopyFeedback({ message: copySuccessLabel, severity: 'success' });
    } catch (copyError) {
      console.error('Error copying API snippet:', copyError);
      setCopyFeedback({ message: copyErrorLabel, severity: 'error' });
    }
    window.setTimeout(() => setCopyFeedback(null), 2200);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderRadius: 2.5,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: '0 18px 60px rgba(0, 0, 0, 0.12)',
        my: 3.5,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="subtitle2">{title}</Typography>
        <Button size="small" startIcon={<ContentCopy />} onClick={() => void handleCopy()}>
          {copyLabel}
        </Button>
      </Stack>
      {copyFeedback && (
        <Alert severity={copyFeedback.severity} sx={{ mx: 2, mt: 2 }}>
          {copyFeedback.message}
        </Alert>
      )}
      <Box sx={{ pb: 2 }}>
        {children}
      </Box>
    </Paper>
  );
}

function getResponseStatusTone(status: number): ChipProps['color'] {
  if (status >= 200 && status < 300) {
    return 'success';
  }
  if (status >= 500) {
    return 'error';
  }
  return 'warning';
}

function getResponseStatusLabel(t: ReturnType<typeof useTranslation>['t'], status: number): string {
  switch (status) {
    case 200:
      return t('pages:help.apiReference.statusLabels.ok');
    case 401:
      return t('pages:help.apiReference.statusLabels.unauthorized');
    case 403:
      return t('pages:help.apiReference.statusLabels.forbidden');
    case 422:
      return t('pages:help.apiReference.statusLabels.unprocessableEntity');
    case 429:
      return t('pages:help.apiReference.statusLabels.tooManyRequests');
    default:
      return t('pages:help.apiReference.statusLabels.generic');
  }
}

function buildResponseTabId(response: ApiReferenceExampleResponse, index: number): string {
  return `${response.status}:${index}`;
}

/**
 * Render request examples with a language selector similar to API reference
 * products such as ElevenLabs/Fern.
 */
export function ApiCodeExamples({ exampleId }: ApiCodeExamplesProps) {
  const { t } = useTranslation(['pages', 'common']);
  const example = API_REFERENCE_EXAMPLES[exampleId];
  const availableLanguages = useMemo(
    () => API_REFERENCE_CODE_LANGUAGE_ORDER.filter((language) => Boolean(example.examples[language])),
    [example.examples],
  );
  const [selectedLanguage, setSelectedLanguage] = useState<ApiReferenceCodeLanguage>(availableLanguages[0] || 'curl');
  const activeLanguage = availableLanguages.includes(selectedLanguage)
    ? selectedLanguage
    : availableLanguages[0] || 'curl';

  const tabs = useMemo<ButtonTab[]>(
    () => availableLanguages.map((language) => ({
      id: language,
      label: API_REFERENCE_CODE_LANGUAGE_LABELS[language],
      icon: <Terminal fontSize="small" />,
      content: <HelpCodeBlock code={example.examples[language] || ''} />,
    })),
    [availableLanguages, example.examples],
  );
  const activeSnippet = example.examples[activeLanguage] || '';

  return (
    <ApiReferenceCard
      title={t('pages:help.apiReference.requestExamples')}
      currentSnippet={activeSnippet}
      copyLabel={t('pages:help.apiReference.copySnippet')}
      copySuccessLabel={t('pages:help.apiReference.copySnippetSuccess')}
      copyErrorLabel={t('pages:help.apiReference.copySnippetError')}
    >
      <Stack spacing={0}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={example.method} color="success" size="small" />
            <Typography
              component="code"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'background.default',
                fontFamily: 'monospace',
                fontSize: 13,
              }}
            >
              {example.fullUrl}
            </Typography>
          </Stack>
        </Stack>
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <ButtonTabs
            tabs={tabs}
            value={activeLanguage}
            onChange={(_, next) => setSelectedLanguage(next as ApiReferenceCodeLanguage)}
            tabsSx={{ mt: 0, '& .MuiTabs-flexContainer': { gap: 1 } }}
            contentSx={{ mt: 1.5 }}
            scrollable
          />
        </Box>
      </Stack>
    </ApiReferenceCard>
  );
}

/**
 * Render example payloads for multiple HTTP outcomes with explicit status tabs.
 */
export function ApiResponseExamples({ exampleId }: ApiResponseExamplesProps) {
  const { t } = useTranslation(['pages', 'common']);
  const example = API_REFERENCE_EXAMPLES[exampleId];
  const fallbackResponseId = buildResponseTabId(example.responses[0] || { status: 200, body: '' }, 0);
  const [selectedResponseId, setSelectedResponseId] = useState(fallbackResponseId);
  const activeResponseId = example.responses.some((response, index) => (
    buildResponseTabId(response, index) === selectedResponseId
  ))
    ? selectedResponseId
    : fallbackResponseId;

  const tabs = useMemo<ButtonTab[]>(
    () => {
      const statusTotals = example.responses.reduce<Map<number, number>>((totals, response) => {
        totals.set(response.status, (totals.get(response.status) || 0) + 1);
        return totals;
      }, new Map<number, number>());
      const statusOrdinals = new Map<number, number>();

      return example.responses.map((response: ApiReferenceExampleResponse, index) => {
        const ordinal = (statusOrdinals.get(response.status) || 0) + 1;
        statusOrdinals.set(response.status, ordinal);
        const hasDuplicateStatus = (statusTotals.get(response.status) || 0) > 1;

        return {
          id: buildResponseTabId(response, index),
          label: (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={response.status}
                size="small"
                color={getResponseStatusTone(response.status)}
                icon={response.status >= 200 && response.status < 300
                  ? <CheckCircleOutline fontSize="small" />
                  : <ErrorOutline fontSize="small" />}
              />
              <Typography variant="body2">
                {getResponseStatusLabel(t, response.status)}
                {hasDuplicateStatus ? ` #${ordinal}` : ''}
              </Typography>
            </Stack>
          ),
          content: <HelpCodeBlock code={response.body} />,
        };
      });
    },
    [example.responses, t],
  );
  const activeResponse = example.responses.find((response, index) => (
    buildResponseTabId(response, index) === activeResponseId
  ));

  return (
    <ApiReferenceCard
      title={t('pages:help.apiReference.responseExamples')}
      currentSnippet={activeResponse?.body || ''}
      copyLabel={t('pages:help.apiReference.copySnippet')}
      copySuccessLabel={t('pages:help.apiReference.copySnippetSuccess')}
      copyErrorLabel={t('pages:help.apiReference.copySnippetError')}
    >
      <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <ButtonTabs
            tabs={tabs}
            value={activeResponseId}
            onChange={(_, next) => setSelectedResponseId(next)}
          tabsSx={{ mt: 0, '& .MuiTabs-flexContainer': { gap: 1 } }}
          contentSx={{ mt: 1.5 }}
          scrollable
        />
      </Box>
    </ApiReferenceCard>
  );
}
